import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getStudent, updateStudent, getCourses, enrollStudent, issueCertificate, deleteStudent } from '../../../api/adminApi';
import Toast from '../../../components/admin/Toast';
import ConfirmModal from '../../../components/admin/ConfirmModal';
import { toTitleCase } from '../../../utils/format';

const AV_COLORS = ['#E8440A','#F7C91B','#1A7048','#5B6ABF','#D4882A','#8A6FB0','#C74D3A','#2E8B8B'];
function avColor(id) {
  let h = 0;
  for (let i = 0; i < String(id).length; i++) h = String(id).charCodeAt(i) + ((h << 5) - h);
  return AV_COLORS[Math.abs(h) % AV_COLORS.length];
}
function initials(name = '') { return name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase(); }
function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function StudentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data,        setData]        = useState(null);
  const [toast,       setToast]       = useState(null);
  const [showDelete,  setShowDelete]  = useState(false);

  // Edit modal
  const [editModal, setEditModal] = useState(false);
  const [editForm,  setEditForm]  = useState({ full_name: '' });
  const [saving,    setSaving]    = useState(false);

  // Enroll modal
  const [enrollModal, setEnrollModal] = useState(false);
  const [courses,     setCourses]     = useState([]);
  const [selCourses,  setSelCourses]  = useState(new Set());
  const [enrolling,   setEnrolling]   = useState(false);

  // Issue cert
  const [issuingId,   setIssuingId]   = useState(null);

  const load = () =>
    getStudent(id).then(d => {
      setData(d);
      setEditForm({ full_name: d.student.full_name });
    }).catch(console.error);

  useEffect(() => { load(); }, [id]);

  function openEnroll() {
    setCourses([]);
    setSelCourses(new Set());
    getCourses().then(d => {
      setCourses(d.courses); // all courses — enrolled ones shown as ticked/disabled
    }).catch(console.error);
    setEnrollModal(true);
  }

  function toggleCourse(cid) {
    setSelCourses(prev => {
      const next = new Set(prev);
      next.has(cid) ? next.delete(cid) : next.add(cid);
      return next;
    });
  }

  async function handleUpdate() {
    setSaving(true);
    try {
      await updateStudent(id, editForm);
      setEditModal(false);
      setToast('Profile updated');
      load();
    } catch (err) {
      setToast({ msg: err.message, type: 'error' });
    } finally { setSaving(false); }
  }

  async function handleEnroll() {
    if (selCourses.size === 0) return;
    // Guard: only Published courses within capacity
    const invalid = [...selCourses].map(cid => courses.find(c => String(c.id) === cid)).filter(c =>
      !c || c.status !== 'Published' || (c.enrolled_count ?? 0) >= (c.capacity ?? 30)
    );
    if (invalid.length > 0) {
      setToast({ msg: 'Some selected courses are not eligible for enrollment (Draft, Archived, or Full).', type: 'error' });
      return;
    }
    setEnrolling(true);
    let enrolled = 0, failed = 0, lastErr = '';
    for (const cid of selCourses) {
      try {
        await enrollStudent({ student_id: Number(id), course_id: Number(cid) });
        enrolled++;
      } catch (err) {
        lastErr = err.message;
        failed++;
      }
    }
    setEnrolling(false);
    setEnrollModal(false);
    load();
    if (failed === 0) {
      setToast(`Enrolled in ${enrolled} course${enrolled !== 1 ? 's' : ''} successfully`);
    } else if (enrolled === 0) {
      setToast({ msg: lastErr || 'Enrollment failed', type: 'error' });
    } else {
      setToast({ msg: `${enrolled} enrolled, ${failed} failed: ${lastErr}`, type: 'error' });
    }
  }

  async function handleIssueCert(enrollmentId) {
    setIssuingId(enrollmentId);
    try {
      await issueCertificate(enrollmentId);
      setToast('Certificate issued successfully');
      load();
    } catch (err) {
      setToast({ msg: err.message, type: 'error' });
    } finally { setIssuingId(null); }
  }

  if (!data) return <div className="body" style={{ color: 'var(--muted)', padding: 40 }}>Loading…</div>;

  const { student, enrollments } = data;
  const color = avColor(student.id);
  const totalCerts = enrollments.filter(e => e.certificate_code).length;
  const avgPct = enrollments.length
    ? Math.round(enrollments.reduce((a, e) => a + (e.progress_percent || 0), 0) / enrollments.length)
    : 0;

  return (
    <>
      {/* ── Header ── */}
      <div className="ph">
        <div>
          <div className="bc">Admin / Students / {toTitleCase(student.full_name)}</div>
          <div className="pt">{toTitleCase(student.full_name)}</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-o btn-sm" onClick={() => navigate('/admin/students')}>← All Students</button>
          <button className="btn btn-d btn-sm" onClick={() => setShowDelete(true)}>🗑 Delete</button>
        </div>
      </div>

      <div className="body">

        {/* ── Profile Header ── */}
        <div className="profile-header" style={{ animation: 'slideUp .3s ease' }}>
          <div className="av" style={{
            width: 64, height: 64, fontSize: 22, flexShrink: 0,
            background: color, color: 'white',
          }}>
            {initials(student.full_name)}
          </div>
          <div className="profile-info" style={{ flex: 1 }}>
            <div style={{ fontFamily: "'Nunito Sans',sans-serif", fontWeight: 800, fontSize: 20, marginBottom: 2 }}>
              {toTitleCase(student.full_name)}
            </div>
            <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 8 }}>{student.email}</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <span className={`b ${student.status === 'Active' ? 'b-g' : 'b-gr'}`}>{student.status || 'Active'}</span>
              <span className="b b-y">Joined {fmtDate(student.joined_at)}</span>
              <span className="b b-o">{enrollments.length} course{enrollments.length !== 1 ? 's' : ''}</span>
              {totalCerts > 0 && (
                <span className="b b-g">🎓 {totalCerts} certificate{totalCerts !== 1 ? 's' : ''}</span>
              )}
            </div>
          </div>
          {/* Edit + Enroll buttons */}
          <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
            <button className="btn btn-o btn-sm" onClick={() => setEditModal(true)}>Edit</button>
            {student.status !== 'Active' ? (
              <span
                className="btn btn-o btn-sm"
                style={{ opacity: 0.45, cursor: 'not-allowed' }}
                title="Student account is Inactive — activate before enrolling"
              >
                + Enroll
              </span>
            ) : (
              <button className="btn btn-o btn-sm" onClick={openEnroll}>+ Enroll</button>
            )}
          </div>
        </div>

        {/* ── Stats ── */}
        <div className="stats" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginTop: 16 }}>
          <div className="sc">
            <div className="sc-l">Avg Progress</div>
            <div className="sc-v">{avgPct}%</div>
            <div className="sc-bar">
              <div className="sc-fill" style={{ width: `${avgPct}%`, background: 'var(--yellow)' }} />
            </div>
          </div>
          <div className="sc">
            <div className="sc-l">Courses</div>
            <div className="sc-v">{enrollments.length}</div>
          </div>
          <div className="sc">
            <div className="sc-l">Certificates</div>
            <div className="sc-v">{totalCerts}</div>
          </div>
          <div className="sc">
            <div className="sc-l">Status</div>
            <div className="sc-v" style={{ fontSize: 14 }}>
              <span className={`b ${student.status === 'Active' ? 'b-g' : 'b-gr'}`}>{student.status || 'Active'}</span>
            </div>
          </div>
        </div>

        {/* ── Enrollment Cards ── */}
        {enrollments.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: 40, color: 'var(--muted)', fontSize: 13 }}>
            Not enrolled in any courses yet.
          </div>
        ) : (
          enrollments.map((e, ei) => {
            const scores = e.quiz_scores?.map(q => q.score).filter(s => s !== null) ?? [];
            const avgQuiz = scores.length ? Math.round(scores.reduce((a, v) => a + v, 0) / scores.length) : null;
            const submittedCount = e.submissions?.filter(s => s.is_submitted).length ?? 0;
            const totalAssign = e.submissions?.length ?? 0;

            // Issue cert eligibility
            const allQuizPassed = (e.quiz_scores?.length ?? 0) > 0 && e.quiz_scores.every(q => q.score !== null && q.score >= 70);
            const allGraded     = (e.submissions?.length ?? 0) > 0 && e.submissions.every(s => s.grade);
            const canIssueCert  = e.progress_percent === 100 && allQuizPassed && allGraded && !e.certificate_code;

            return (
              <div key={e.enrollment_id} className="card" style={{ marginBottom: 14, animationDelay: `${ei * 0.08}s` }}>

                {/* Card header */}
                <div className="ct">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 16 }}>{e.course.emoji}</span>
                    <span style={{ cursor: 'pointer' }} onClick={() => navigate(`/admin/courses/${e.course.id}`)}>
                      {e.course.title}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {e.certificate_code && <span className="b b-g">🎓 Certified</span>}
                    <span className="b b-y">{e.progress_percent}%</span>
                  </div>
                </div>

                {/* Progress bar */}
                <div style={{ marginBottom: 14 }}>
                  <div className="pbar" style={{ height: 6 }}>
                    <div className="pfill" style={{ width: `${e.progress_percent}%` }} />
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>
                    {e.modules_completed}/{e.modules_total} modules
                    {avgQuiz !== null ? ` · Avg quiz: ${avgQuiz}%` : ''}
                  </div>
                </div>

                {/* Quiz Scores */}
                {e.quiz_scores?.length > 0 && (
                  <details style={{ marginBottom: 8 }}>
                    <summary style={{ fontSize: 12.5, fontWeight: 600, cursor: 'pointer', padding: '8px 0', color: 'var(--ink)' }}>
                      Quiz Scores by Module
                    </summary>
                    <div style={{ padding: '4px 0' }}>
                      {e.quiz_scores.map((q, qi) => (
                        <div key={q.module_id} style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          padding: '8px 0',
                          borderBottom: qi < e.quiz_scores.length - 1 ? '1px solid var(--border)' : 'none',
                        }}>
                          <span style={{ fontSize: 12.5 }}>Module {qi + 1}: {q.module_title}</span>
                          <span className={`b ${q.score === null ? 'b-gr' : q.score >= 70 ? 'b-g' : 'b-r'}`}>
                            {q.score === null ? 'Not taken' : `${q.score}%`}
                          </span>
                        </div>
                      ))}
                    </div>
                  </details>
                )}

                {/* Assignment Submissions */}
                {e.submissions?.length > 0 && (
                  <details style={{ marginBottom: 8 }}>
                    <summary style={{ fontSize: 12.5, fontWeight: 600, cursor: 'pointer', padding: '8px 0', color: 'var(--ink)' }}>
                      Assignment Submissions ({submittedCount}/{totalAssign})
                    </summary>
                    <div style={{ padding: '4px 0' }}>
                      {e.submissions.map((sub, si) => (
                        <div key={sub.assignment_id} style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          padding: '10px 0',
                          borderBottom: si < e.submissions.length - 1 ? '1px solid var(--border)' : 'none',
                        }}>
                          <div>
                            <div style={{ fontSize: 12.5, fontWeight: 500 }}>{sub.title}</div>
                            {sub.due_date && (
                              <div style={{ fontSize: 11, color: 'var(--muted)' }}>Due {fmtDate(sub.due_date)}</div>
                            )}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            {sub.file_name && (
                              <span style={{ fontSize: 11, color: 'var(--muted)' }}>{sub.file_name}</span>
                            )}
                            {sub.grade
                              ? <span className="b b-g">{sub.grade}</span>
                              : sub.is_submitted
                              ? <span className="b b-o">Submitted</span>
                              : <span className="b b-gr">Not submitted</span>
                            }
                          </div>
                        </div>
                      ))}
                    </div>
                  </details>
                )}

                {/* Certificate section */}
                {e.certificate_code ? (
                  <div style={{
                    padding: 14, background: 'var(--green-soft)',
                    border: '1px solid var(--green-border)', borderRadius: 'var(--rs)',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8,
                  }}>
                    <div>
                      <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 2 }}>Certificate Issued</div>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{fmtDate(e.certificate_issued_at)}</div>
                      <div style={{ fontSize: 11, color: 'var(--muted)', fontFamily: "'DM Mono',monospace" }}>
                        {e.certificate_code}
                      </div>
                    </div>
                    <button
                      className="btn btn-o btn-sm"
                      onClick={() => navigate(`/certificate/${e.certificate_code}`)}
                    >
                      View Certificate
                    </button>
                  </div>
                ) : canIssueCert ? (
                  <button
                    className="btn btn-sm"
                    style={{
                      marginTop: 8,
                      background: 'var(--green, #1A7048)', color: 'white',
                      border: 'none', borderRadius: 'var(--rs)',
                      padding: '8px 14px', fontSize: 12.5,
                      cursor: issuingId === e.enrollment_id ? 'wait' : 'pointer',
                    }}
                    disabled={issuingId === e.enrollment_id}
                    onClick={() => handleIssueCert(e.enrollment_id)}
                  >
                    {issuingId === e.enrollment_id ? 'Issuing…' : '🎓 Issue Certificate'}
                  </button>
                ) : (
                  <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 8, fontStyle: 'italic' }}>
                    Certificate requirements not yet met
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* ── Edit Student Modal ── */}
      {editModal && (
        <div className="modal-overlay">
          <div className="modal-box" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
            <div className="modal-title">
              Edit Student
              <button className="btn btn-o btn-sm" onClick={() => setEditModal(false)}>✕</button>
            </div>
            <div className="fg">
              <label className="fl">Full Name</label>
              <input className="li" placeholder="e.g. Alex Johnson" value={editForm.full_name || ''}
                onChange={e => setEditForm(f => ({ ...f, full_name: e.target.value }))} />
            </div>
            <div className="fg" style={{ marginBottom: 0 }}>
              <label className="fl">Email</label>
              <input className="li" value={student.email} readOnly
                style={{ background: '#f8f9fa', color: 'var(--muted)', cursor: 'not-allowed' }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
              <button className="btn btn-o" onClick={() => setEditModal(false)}>Cancel</button>
              <button className="btn btn-p" disabled={saving} onClick={handleUpdate}>
                {saving ? 'Saving…' : 'Update'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Enroll in Course Modal ── */}
      {enrollModal && (
        <div className="modal-overlay">
          <div className="modal-box" style={{ maxWidth: 460 }} onClick={e => e.stopPropagation()}>
            <div className="modal-title">
              Enroll in Courses
              <button className="btn btn-o btn-sm" onClick={() => setEnrollModal(false)}>✕</button>
            </div>
            <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 16, marginTop: -10 }}>
              Select courses to enroll <strong style={{ color: 'var(--ink)' }}>{student.full_name}</strong> in
            </div>

            {courses.length === 0 ? (
              <div style={{ fontSize: 12.5, color: 'var(--muted)', padding: '10px 0' }}>Loading…</div>
            ) : (() => {
              const enrolledIds = new Set(enrollments.map(e => e.course.id));
              // Selectable: Published + has capacity remaining + not already enrolled
              const selectable  = courses.filter(c =>
                !enrolledIds.has(c.id) &&
                c.status === 'Published' &&
                (c.enrolled_count ?? 0) < (c.capacity ?? 30)
              );
              // Disabled (not enrolled but can't be selected)
              const disabled    = courses.filter(c =>
                !enrolledIds.has(c.id) && (
                  c.status !== 'Published' ||
                  (c.enrolled_count ?? 0) >= (c.capacity ?? 30)
                )
              );
              const alreadyIn   = courses.filter(c => enrolledIds.has(c.id));
              const allSelected = selectable.length > 0 && selectable.every(c => selCourses.has(String(c.id)));

              const courseIcon = c => (
                <div style={{
                  width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                  background: `${c.accent_color || '#5B6ABF'}22`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16,
                }}>
                  {c.emoji || '📚'}
                </div>
              );

              return (
                <>
                  {selectable.length > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                      <span style={{ fontSize: 12, color: 'var(--muted)' }}>
                        {selCourses.size > 0 ? `${selCourses.size} selected` : `${selectable.length} available`}
                      </span>
                      <button
                        className="btn btn-o btn-sm"
                        style={{ fontSize: 11 }}
                        onClick={() => allSelected ? setSelCourses(new Set()) : setSelCourses(new Set(selectable.map(c => String(c.id))))}
                      >
                        {allSelected ? 'Deselect All' : 'Select All'}
                      </button>
                    </div>
                  )}

                  <div className="course-pick-list" style={{ maxHeight: 320, overflowY: 'auto' }}>
                    {/* Selectable — Published + has space */}
                    {selectable.map(c => {
                      const sel = selCourses.has(String(c.id));
                      return (
                        <div key={c.id} className={`course-pick-item${sel ? ' selected' : ''}`} onClick={() => toggleCourse(String(c.id))}>
                          <div className="course-pick-check">{sel ? '✓' : ''}</div>
                          {courseIcon(c)}
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 13.5 }}>{toTitleCase(c.title)}</div>
                            <div style={{ fontSize: 11, color: 'var(--muted)' }}>
                              {c.enrolled_count ?? 0}/{c.capacity ?? 30} enrolled
                            </div>
                          </div>
                        </div>
                      );
                    })}

                    {/* Disabled — Draft / Archived / Full */}
                    {disabled.map(c => {
                      const isFull   = (c.enrolled_count ?? 0) >= (c.capacity ?? 30);
                      const reason   = isFull ? 'Full' : c.status;
                      return (
                        <div key={c.id} className="course-pick-item" style={{ opacity: 0.45, cursor: 'not-allowed' }} title={`Cannot enroll — course is ${reason}`}>
                          <div className="course-pick-check" />
                          {courseIcon(c)}
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 13.5 }}>{toTitleCase(c.title)}</div>
                            <div style={{ fontSize: 11, color: 'var(--muted)' }}>
                              {isFull ? `Full · ${c.enrolled_count}/${c.capacity ?? 30}` : c.status}
                            </div>
                          </div>
                        </div>
                      );
                    })}

                    {/* Already enrolled */}
                    {alreadyIn.map(c => (
                      <div key={c.id} className="course-pick-item" style={{ opacity: 0.55, cursor: 'default' }}>
                        <div className="course-pick-check" style={{ color: '#1A7048', background: '#e6f4ee', borderColor: '#1A7048' }}>✓</div>
                        {courseIcon(c)}
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 13.5 }}>{toTitleCase(c.title)}</div>
                          <div style={{ fontSize: 11, color: 'var(--muted)' }}>Already enrolled</div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {selectable.length === 0 && disabled.length === 0 && alreadyIn.length > 0 && (
                    <div style={{ fontSize: 12.5, color: 'var(--muted)', textAlign: 'center', padding: '8px 0' }}>
                      Already enrolled in all available courses.
                    </div>
                  )}
                  {selectable.length === 0 && (disabled.length > 0 || alreadyIn.length === 0) && (
                    <div style={{ fontSize: 12.5, color: 'var(--muted)', textAlign: 'center', padding: '8px 0' }}>
                      No published courses with available capacity.
                    </div>
                  )}
                </>
              );
            })()}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
              <button className="btn btn-o" onClick={() => setEnrollModal(false)}>Cancel</button>
              <button className="btn btn-p" disabled={enrolling || selCourses.size === 0} onClick={handleEnroll}>
                {enrolling ? 'Enrolling…' : selCourses.size > 0 ? `Enroll in ${selCourses.size} Course${selCourses.size !== 1 ? 's' : ''}` : 'Enroll'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <Toast
          message={typeof toast === 'string' ? toast : toast.msg}
          type={typeof toast === 'string' ? 'success' : toast.type}
          onDone={() => setToast(null)}
        />
      )}

      {showDelete && (
        <ConfirmModal
          title="Delete Student?"
          message="This student will be removed from the platform. All their progress and submissions will be retained in the database."
          confirmLabel="Yes, Delete"
          onCancel={() => setShowDelete(false)}
          onConfirm={async () => {
            try { await deleteStudent(student.id); navigate('/admin/students'); }
            catch (err) { setToast({ msg: err.message, type: 'error' }); setShowDelete(false); }
          }}
        />
      )}
    </>
  );
}
